/** Trading Intelligence — composed gap screen (SDD §7.3, OD-1: "dedicated screen, composed from the AI Insights screen's proven
 *  patterns — deltas, flags, evidence lists — under the package's design language"). The reference carries no markup for this
 *  route, so this view is hand-written in the canonical design language (CLAUDE.md of the package) and reads the same model the
 *  AI Insights screen reads. Its visual baseline is generated at owner approval (tracker ESC-003); it is not compared against
 *  the reference. Style-relative rule (R-04): findings never penalise frequency or count. */
import * as dc from '@/lib/dc';
import type { ViewModel } from '@/screens/generated/view-model';
import type { CSSProperties } from 'react';

const card: CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--card-line)',
  boxShadow: 'var(--shadow-card)',
  borderRadius: '16px',
  padding: '14px 18px 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '11px',
  minWidth: 0,
};
const label: CSSProperties = {
  fontSize: '9.5px',
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  color: 'var(--muted2)',
  fontWeight: 600,
};
const micro: CSSProperties = {
  fontSize: '9px',
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  color: 'var(--muted2)',
  fontWeight: 600,
};
const inset: CSSProperties = {
  border: '1px solid var(--l8)',
  borderRadius: '11px',
  background: 'var(--l3)',
  padding: '10px 12px',
};
const num: CSSProperties = { fontVariantNumeric: 'tabular-nums' };

export function TradingIntelView({ m }: { m: ViewModel }) {
  const deltas = m.aiDeltas ?? [];
  const flags = m.aiFlags ?? [];
  const improved = m.aiImproved ?? [];
  const regressed = m.aiRegressed ?? [];
  const insights = m.aiInsights ?? [];
  const significant = deltas.filter((d) => d?.d && String(d.d) !== 'no change');
  return (
    <div style={{ padding: '24px 12px 40px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={label}>Analyse · Trading Intelligence</div>
          <div style={{ fontSize: '15px', fontWeight: 500, letterSpacing: '-0.01em', marginTop: '7px' }}>
            Cross-metric findings, with their evidence
          </div>
          <div style={{ fontSize: '11.5px', color: 'var(--muted)', marginTop: '4px', maxWidth: '70ch' }}>
            Each finding states its effect size and sample; findings below significance are suppressed rather
            than hedged. Style-relative: a scalper is never penalised for count, a swing trader never for
            frequency.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            padding: '3px',
            borderRadius: '99px',
            border: '1px solid var(--l8)',
          }}
        >
          {(m.aiPeriods ?? []).map((p, i) => (
            <button
              key={i}
              onClick={p?.go}
              style={dc.css(
                `padding:4px 11px;border-radius:99px;border:none;background:${dc.s(p?.bg)};color:${dc.s(p?.fg)};font-size:10.5px;font-weight:500;cursor:pointer;white-space:nowrap`,
              )}
            >
              {dc.t(p?.label)}
            </button>
          ))}
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(400px,1fr))',
          gap: '12px',
          alignItems: 'stretch',
        }}
      >
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '9px' }}>
            <span style={label}>Findings</span>
            <span style={{ fontSize: '10.5px', color: 'var(--faint)' }}>
              {significant.length === 1 ? '1 finding' : significant.length + ' findings'} ·{' '}
              {deltas.length - significant.length} suppressed
            </span>
          </div>
          {significant.length === 0 ? (
            <div style={{ ...inset, fontSize: '11.5px', color: 'var(--muted)' }}>
              Nothing crosses the significance floor in this window — no finding is better than an honest
              blank.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {significant.map((d, i) => (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.3fr 0.8fr 0.8fr 0.8fr',
                    gap: '10px',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderTop: i ? '1px solid var(--l6)' : 'none',
                    fontSize: '11.5px',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className={`ph ${dc.s(d?.icon)}`} style={{ fontSize: '13px', color: dc.a(d?.fg) }}></i>
                    {dc.t(d?.label)}
                  </span>
                  <span style={{ ...num, color: 'var(--muted)' }}>{dc.t(d?.was)}</span>
                  <span style={{ ...num }}>{dc.t(d?.now)}</span>
                  <span style={{ ...num, fontWeight: 600, color: dc.a(d?.fg), textAlign: 'right' }}>
                    {dc.t(d?.d)}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              gap: '14px',
              paddingTop: '9px',
              borderTop: '1px solid var(--l6)',
              fontSize: '10.5px',
              color: 'var(--muted)',
            }}
          >
            <span>
              <span style={micro}>Improved</span> · {improved.length}
            </span>
            <span>
              <span style={micro}>Regressed</span> · {regressed.length}
            </span>
          </div>
        </div>
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '9px' }}>
            <span style={label}>Risk flags</span>
            <span style={{ fontSize: '10.5px', color: 'var(--faint)' }}>{dc.t(m.aiFlagCount)}</span>
          </div>
          {flags.length === 0 ? (
            <div style={{ ...inset, fontSize: '11.5px', color: 'var(--muted)' }}>
              No tiered flags for this window.
            </div>
          ) : (
            flags.map((f, i) => (
              <div
                key={i}
                style={{
                  ...inset,
                  borderColor: dc.a(f?.bd) ?? 'var(--l8)',
                  background: dc.a(f?.bg) ?? 'var(--l3)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ ...micro, color: dc.a(f?.fg) }}>{dc.t(f?.sev)}</span>
                  <span style={{ fontSize: '12px', fontWeight: 600 }}>{dc.t(f?.title)}</span>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-mid)' }}>{dc.t(f?.detail)}</div>
              </div>
            ))
          )}
        </div>
      </div>
      <div style={card}>
        <div style={label}>Evidence</div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {insights.map((ins, i) => (
            <div key={i} style={{ padding: '8px 0', borderTop: i ? '1px solid var(--l6)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                <i
                  className={`ph ${dc.s(ins?.icon)}`}
                  style={{ fontSize: '13px', color: 'var(--muted2)' }}
                ></i>
                <span style={{ fontWeight: 500 }}>{dc.t(ins?.title)}</span>
                <span style={{ ...micro, color: dc.a(ins?.sevFg) }}>{dc.t(ins?.sev)}</span>
              </div>
              <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                {(ins?.refs ?? []).map((r, j) => (
                  <span
                    key={j}
                    style={{
                      fontSize: '10px',
                      padding: '3px 9px',
                      borderRadius: '99px',
                      border: '1px solid var(--l8)',
                      color: 'var(--muted)',
                    }}
                  >
                    {dc.t(r?.label)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            fontSize: '9.5px',
            color: 'var(--faint)',
            paddingTop: '9px',
            borderTop: '1px solid var(--l6)',
          }}
        >
          Composed gap screen (OD-1) · baseline frozen at owner approval · findings emit suggestions into the
          next Session Setup
        </div>
      </div>
    </div>
  );
}
